import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Alert, Collapse } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FormFieldWithLabelFactory } from '../../../../../components/Form/FormFieldAndLabelFactory';
import {
  ManageCompanyFieldsTypes,
  manageSingleCompanyFormSchema,
} from '../../../../../schemas/template-assignment-schema';
import { clientConfigFieldsAtom } from '../../../../../services/state/AppConfigStateJ';
import { useCompanyOnboardedStatus } from '../../../hooks/useRespondentApprovalsEnabled';
import { MaggieFeatureFlags } from '../../../../../util/feature-flags';
import { schemaToFormFields } from '../../../../../util/schema-utils';
import { allCompanyFinancialsStateLoadable } from '../../../../CompanyProfiles/state/CompanyFinancialsState';
import { PortcoRespondentsField } from './PortcoRespondentsField';

export function ManageSingleCompanyFields({ companyId }: { companyId: number }) {
  const fields = useMemo(
    () => new Map(schemaToFormFields(manageSingleCompanyFormSchema()).map((f) => [f.key, f])),
    []
  );
  const showFyePrompt = useShowFyePrompt(companyId);

  const isConsolidated =
    (useAtomValue(clientConfigFieldsAtom)?.Using_Consolidated_Data_Collection as boolean) ?? false;
  const { useStarbreakerCollectionV2 } = useFlags<MaggieFeatureFlags>();
  const isConsolidatedV2 = isConsolidated && useStarbreakerCollectionV2;
  const { isOnboarded, companyStatuses } = useCompanyOnboardedStatus(companyId);

  return (
    <>
      <div>
        <FormFieldWithLabelFactory key={'fye'} formField={fields.get('fye')!} />
        <Collapse in={showFyePrompt} sx={showFyePrompt ? { mt: '1rem' } : undefined}>
          {showFyePrompt ? (
            <>
              <Alert severity='warning' sx={{ mb: '0.5rem' }}>
                This company already has KPI data entered for previous periods.
                <br /> Would you like to adjust those KPIs to the new FYE?
              </Alert>
              <FormFieldWithLabelFactory key={'fye'} formField={fields.get('adjustHistoricKPIs')!} />
            </>
          ) : (
            <div style={{ height: '3rem' }} />
          )}
        </Collapse>
      </div>
      {isOnboarded ? (
        <PortcoRespondentsField companyStatuses={companyStatuses} />
      ) : (
        <FormFieldWithLabelFactory key={'respondents'} formField={fields.get('respondents')!} />
      )}
      {!isConsolidated && (
        <FormFieldWithLabelFactory
          key={'secondaryRespondents'}
          formField={fields.get('secondaryRespondents')!}
        />
      )}
      {isConsolidatedV2 && <FormFieldWithLabelFactory key='domain' formField={fields.get('domain')!} />}
    </>
  );
}

export function useShowFyePrompt(companyId: number) {
  const {
    control,
    formState: { defaultValues },
  } = useFormContext<ManageCompanyFieldsTypes>();
  const fye = useWatch({ name: 'fye', control });
  const financialsLoadable = useAtomValue(allCompanyFinancialsStateLoadable(companyId));
  const hasExistingKPIs =
    financialsLoadable.state === 'hasData' ? (financialsLoadable.data?.length ?? 0) > 0 : false;

  return hasExistingKPIs && fye !== defaultValues?.fye;
}
